import hexRgb from 'hex-rgb';
import config from '@/config';
import filters from '@/filters';
import i18n from '@/i18n';
import { getCalendarIcons } from '@/modules/calendar/helpers';

const Item = ({ event, bufferTime }) => {
  const isWaitingList = event.extendedProps.type === 'waiting_list';
  const isDynamicPrice = event.extendedProps.type === 'dynamic_price';
  const isPreview = !!event.extendedProps.preview;
  const isRequest = !!event.extendedProps.request;

  const customerMemberName = (customerMember) => {
    if (!customerMember) {
      return;
    }
    return `${customerMember.firstName} ${customerMember.lastName}`;
  };

  const item = {
    allDay: event.allDay,
    title: event.title,
    customerName:
      event.extendedProps.type === 'appointment'
        ? customerMemberName(event.extendedProps.customerMember) ||
          event.extendedProps.customerName ||
          i18n.t('calendar.walk_in')
        : '',
    color:
      event.extendedProps.type === 'absence' && !event.extendedProps.chore
        ? '#aaaaaa'
        : event.extendedProps.color,
    showInHeader:
      event.extendedProps.type === 'note' ||
      event.extendedProps.type === 'holiday' ||
      (event.extendedProps.type === 'absence' && event.allDay) ||
      isWaitingList,
    isSmall: event.isSmall,
    isMedium: event.isMedium,
    isLarge: !event.isSmall && !event.isMedium,
  };

  const startTime = event.startStr ? filters.time(event.startStr) : '';
  const endTime = event.endStr ? filters.time(event.endStr) : '';
  const timeText =
    event.timeText ||
    `${startTime}${startTime && endTime ? ' - ' : ''}${endTime}`;

  item.timeText = item.isLarge ? timeText : timeText?.split(' - ')[0] || '';

  const isChore =
    event.extendedProps.type === 'absence' && event.extendedProps.chore;

  const icons = item.showInHeader
    ? [
        {
          name: 'message-circle',
          show: event.extendedProps.type === 'note',
        },
        {
          name: 'home',
          show: event.extendedProps.type === 'holiday',
        },
        {
          name: 'checkmark-square',
          show: isChore,
        },
        {
          name: 'slash',
          show:
            event.extendedProps.type === 'absence' &&
            !event.extendedProps.chore,
        },
      ].filter((icon) => icon.show)
    : event.isMonthView
      ? []
      : getCalendarIcons({
          features: event.features,
        });

  let rgbString = 'rgb(242, 243, 251)';
  if (item.color) {
    const rgbArray = hexRgb(item.color, { format: 'array' }).map((val) =>
      Math.round(val + (255 - val) * (1 - (event.darkerColors ? 0.5 : 0.3))),
    );
    rgbString = `rgb(${rgbArray[0]},${rgbArray[1]},${rgbArray[2]})`;
  } else if (isWaitingList || isDynamicPrice) {
    item.color = '#4C49C5';
  }

  if (isRequest) {
    rgbString = 'rgb(255, 255, 255)';
  }

  if (event.isDarkMode && event.extendedProps.type === 'absence') {
    rgbString = 'rgba(0, 0, 0, 0)';
  }

  // In month view timed events are a coloured dot with the time and customer,
  // so a full day reads as a list instead of a stack of coloured blocks
  const isMonthListItem = event.isMonthView && !item.showInHeader;

  let bufferTimePercentage = 0;
  if (
    bufferTime > 0 &&
    event.extendedProps.type === 'appointment' &&
    !isMonthListItem
  ) {
    bufferTimePercentage = (bufferTime / event.duration) * 100;
  }

  return `
    <div class="calendar-item calendar-item--${event.extendedProps.type} ${isChore ? 'calendar-item--chore' : ''} ${item.showInHeader && icons.length ? 'calendar-item--has--icon' : ''} ${item.isSmall ? 'calendar-item--is-small' : ''} ${item.showInHeader ? 'calendar-item--in-header' : ''} ${isPreview ? 'calendar-item--preview' : ''} ${isRequest ? 'calendar-item--request' : ''} ${isMonthListItem ? 'calendar-item--month' : ''}"
      style="border-color: ${item.color}; background-color: ${rgbString};"
      ${config.isTest || config.isDevelopment ? `data-test="${isPreview ? 'calendar-preview-event' : item.showInHeader ? `calendar-${event.extendedProps.type}-header` : `calendar-${event.extendedProps.type}`}"` : ''}
    >
      ${item.allDay ? '' : `${item.isLarge ? `${item.timeText}<br>` : `<span class="calendar-item__time-small">${item.timeText}</span>`}`}
      ${!item.allDay && (item.customerName || event.extendedProps.name) ? `<strong>${item.customerName || event.extendedProps.name}</strong>${item.isSmall ? '' : '<br>'}` : ''}
      ${isMonthListItem && (item.customerName || event.extendedProps.name) ? '' : item.allDay && event.extendedProps.name ? event.extendedProps.name : item.title}
      ${isWaitingList && event.extendedProps.amount ? `<div class="js-calendar-icon calendar-item__amount" data-tooltip-text="${i18n.t('calendar.waiting_list_amount', { amount: event.extendedProps.amount }, event.extendedProps.amount)}">${event.extendedProps.amount}</div>` : ''}
      ${bufferTimePercentage ? `<div class="calendar-item__buffer-time js-calendar-item-buffer-time" style="height: calc(${bufferTimePercentage}% - 1px);"></div>` : ''}
      ${
        icons.length
          ? `
        <div class="calendar-item__icons" style="background-color: ${rgbString}; box-shadow: 0 0 6px 6px ${rgbString};" ${config.isTest || config.isDevelopment ? 'data-test="calendar-event-icons"' : ''}>
          ${icons
            .map((icon) =>
              icon.name === 'smiley_happy'
                ? `
            <svg class="js-calendar-icon" data-tooltip-name="${icon.tooltip?.name || ''}" viewBox="0 0 24 24"><g><path fill-rule="evenodd" clip-rule="evenodd" d="M12 4c-4.411 0-8 3.589-8 8s3.589 8 8 8 8-3.589 8-8-3.589-8-8-8m0 18C6.486 22 2 17.514 2 12S6.486 2 12 2s10 4.486 10 10-4.486 10-10 10" /><path fill-rule="evenodd" clip-rule="evenodd" d="M15 12a1 1 0 00-1 1c0 1.103-.897 2-2 2s-2-.897-2-2a1 1 0 10-2 0c0 2.206 1.794 4 4 4s4-1.794 4-4a1 1 0 00-1-1M15 8a1 1 0 100 2 1 1 0 000-2M9 8a1 1 0 100 2 1 1 0 000-2" /></g></svg>
            `
                : icon.name === 'smiley_sad'
                  ? `
            <svg class="js-calendar-icon" data-tooltip-name="${icon.tooltip?.name || ''}" viewBox="0 0 24 24"><g><path fill-rule="evenodd" clip-rule="evenodd" d="M12 4c-4.411 0-8 3.589-8 8s3.589 8 8 8 8-3.589 8-8-3.589-8-8-8m0 18C6.486 22 2 17.514 2 12S6.486 2 12 2s10 4.486 10 10-4.486 10-10 10" /><path fill-rule="evenodd" clip-rule="evenodd" d="M15 17a1 1 0 01-1-1c0-1.103-.897-2-2-2s-2 .897-2 2a1 1 0 11-2 0c0-2.206 1.794-4 4-4s4 1.794 4 4a1 1 0 01-1 1M15 8a1 1 0 100 2 1 1 0 000-2M9 8a1 1 0 100 2 1 1 0 000-2" /></g></svg>
            `
                  : icon.name === 'treatwell'
                    ? `
            <svg class="js-calendar-icon" data-tooltip-name="${icon.tooltip?.name || ''}" viewBox="0 0 24 24"><g><path d="m5.66259,15.27988c0,1.67739 0.90968,2.68839 2.41074,2.68839c0.47755,0 0.95521,-0.0919 1.47828,-0.27576l0,0.16082c-0.86425,1.35569 -2.09235,2.11394 -3.63884,2.11394c-2.02412,0 -3.50234,-1.28673 -3.50234,-3.72232l0,-6.43354l-1.95587,0l0,-0.25278l4.93509,-4.52648l0.27295,0l0,10.24776l0,-0.00001z" /><path d="m14.6797,19.89832l1.66018,-3.69935l-3.43414,-7.92711l-6.44717,0l0,1.53944l3.5589,0l4.43481,10.08701l0.22742,0l0,0.00001z" /><path d="m15.72585,8.27188l3.54782,0l3.43415,7.9271l-1.66025,3.69935l-0.22737,0l-5.09436,-11.62644l0.00001,0z" /><path d="m23.86766,12.70647c0,-1.10289 -0.50027,-3.30868 -1.06888,-4.43458l3.45688,0l-2.22878,4.43458l-0.15921,0z" /></g></svg>
            `
                    : icon.name === 'syringe'
                      ? `
            <svg class="js-calendar-icon" data-tooltip-name="${icon.tooltip?.name || ''}" viewBox="0 0 24 24"><g><path d="M14.9316 2.29292C15.3221 1.90258 15.9552 1.90246 16.3456 2.29292L21.707 7.65425C22.0974 8.04471 22.0973 8.67777 21.707 9.06831C21.3164 9.45884 20.6834 9.45884 20.2929 9.06831L18.3193 7.09468L15.9032 9.5107L20.1747 13.7822C20.5653 14.1727 20.5653 14.8057 20.1747 15.1962C19.7842 15.5867 19.1512 15.5867 18.7607 15.1962L17.1698 13.6054L10.2177 20.5585C8.9811 21.7949 6.97569 21.7951 5.73918 20.5585L5.2968 20.1162L3.70696 21.707C3.31643 22.0975 2.68342 22.0975 2.29289 21.707C1.90237 21.3165 1.90237 20.6834 2.29289 20.2929L3.88274 18.7021L3.44133 18.2607C2.20482 17.0242 2.20495 15.0188 3.44133 13.7822L10.3935 6.82905L8.80364 5.23921C8.41315 4.84872 8.41322 4.21568 8.80364 3.82515C9.19416 3.43462 9.82717 3.43462 10.2177 3.82515L14.4892 8.09663L16.9052 5.68062L14.9316 3.70698C14.541 3.31646 14.541 2.68345 14.9316 2.29292ZM4.85637 15.1962C4.40084 15.6518 4.40084 16.3901 4.85637 16.8457L7.15422 19.1435C7.60975 19.599 8.34811 19.599 8.80364 19.1435L15.7558 12.1914L11.8085 8.24409L4.85637 15.1962Z"/>
</g></svg>
            `
                      : `<i class="eva eva-${icon.name}-outline js-calendar-icon" data-tooltip-name="${icon.tooltip?.name || ''}" data-tooltip-text="${icon.tooltip?.text || ''}" ${config.isTest || config.isDevelopment ? `data-test="calendar-event-icon-${icon.name}"` : ''}></i>`,
            )
            .join('')}
        </div>
      `
          : ''
      }
    </div>
  `;
};

export default (data) => Item(data);
